import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/finance/taxes.service";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useTaxStore = defineStore("tax", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const loading = ref(false);
  const deletingId = ref(null);
  const meta = ref({ total: 0, per_page: 25, current_page: 1 });

  const filters = reactive({
    q: "", status: "", sort: "recent",
    date_from: "", date_to: "",
    page: 1, per_page: 25,
  });

  function normalize(r) {
    return {
      id: r.id,
      name: r.name,
      rate: r.rate, // number
      status: r.status || "Active",
      effective_from: r.effective_from || null,
      effective_to: r.effective_to || null,
      description: r.description || "",
      created_at: r.created_at,
    };
  }

  async function list(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      items.value = [];
      byId.value = Object.create(null);
      meta.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    loading.value = true;
    try {
      const params = { ...filters, ...extra };
      const { data } = await svc.list(params);
      const rows = data?.data || data || [];
      items.value = rows.map(normalize);
      byId.value = Object.create(null);
      items.value.forEach(n => (byId.value[n.id] = n));
      meta.value = {
        total: data?.meta?.total ?? rows.length,
        per_page: data?.meta?.per_page ?? params.per_page ?? 25,
        current_page: data?.meta?.current_page ?? params.page ?? 1,
      };
      filters.page = meta.value.current_page;
      filters.per_page = meta.value.per_page;
    } catch (e) {
      toast(e?.response?.data?.message || e.message || "Failed to load taxes", "error");
    } finally {
      loading.value = false;
    }
  }

  async function get(id) {
    const { data } = await svc.get(id);
    const n = normalize(data?.data || data);
    byId.value[n.id] = n;
    const i = items.value.findIndex(x => x.id === n.id);
    if (i === -1) items.value.unshift(n); else items.value.splice(i, 1, n);
    return n;
  }

  async function create(payload) {
    const { data } = await svc.create(payload);
    const n = normalize(data?.data || data);
    items.value.unshift(n);
    byId.value[n.id] = n;
    toast("Tax created", "success");
    return n;
  }

  async function update(id, payload) {
    const { data } = await svc.update(id, payload);
    const n = normalize(data?.data || data);
    byId.value[n.id] = n;
    const i = items.value.findIndex(x => x.id === n.id);
    if (i === -1) items.value.unshift(n); else items.value.splice(i, 1, n);
    toast("Tax updated", "success");
    return n;
  }

  async function remove(id) {
    try {
      deletingId.value = id;
      await svc.remove(id);
      items.value = items.value.filter(x => x.id !== id);
      delete byId.value[id];
      toast("Deleted", "success");
    } finally {
      deletingId.value = null;
    }
  }

  const hasMore = () => meta.value.current_page * meta.value.per_page < meta.value.total;

  return { items, byId, loading, deletingId, meta, filters, list, get, create, update, remove, hasMore };
});
